import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Building2, ScrollText } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { Section, Eyebrow, Reveal } from "@/components/site/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { COMPANY } from "@/lib/company";

const COORDS_COPY = {
  fr: {
    officeLabel: "Siège social",
    generalLabel: "Contact général",
    irLabel: "Relations investisseurs",
    companyLabel: "Immatriculation",
    companyNumberPrefix: "Company number",
    registered: "Immatriculée en Angleterre et au Pays de Galles",
  },
  en: {
    officeLabel: "Registered office",
    generalLabel: "General enquiries",
    irLabel: "Investor relations",
    companyLabel: "Registration",
    companyNumberPrefix: "Company number",
    registered: "Registered in England & Wales",
  },
  es: {
    officeLabel: "Sede social",
    generalLabel: "Contacto general",
    irLabel: "Relaciones con inversores",
    companyLabel: "Matriculación",
    companyNumberPrefix: "Número de sociedad",
    registered: "Registrada en Inglaterra y Gales",
  },
};

export default function Contact() {
  const { t, lang } = useI18n();
  const c = COORDS_COPY[lang] || COORDS_COPY.en;
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
    } catch { toast.error(t("contact.error")); }
    finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Section className="pt-28 md:pt-40">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Left — headline + coordinates */}
        <div className="md:col-span-5">
          <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
          <Reveal><h1 className="mt-6 font-display font-light text-5xl md:text-6xl leading-[0.98] tracking-[-0.03em]">{t("contact.title")}</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-8 text-[15px] text-muted-foreground leading-relaxed max-w-md">{t("contact.body")}</p></Reveal>

          <div className="mt-12 space-y-8 text-[14px]" data-testid="contact-coordinates">
            <CoordBlock icon={MapPin} label={c.officeLabel}>
              <p className="font-display text-[16px] tracking-tight text-foreground">{COMPANY.legalName}</p>
              <p className="mt-1 leading-relaxed text-muted-foreground">
                {COMPANY.address.line1}<br />
                {COMPANY.address.city} {COMPANY.address.postcode}<br />
                {COMPANY.address.country}
              </p>
            </CoordBlock>

            <CoordBlock icon={Building2} label={c.companyLabel}>
              <p className="text-muted-foreground leading-relaxed">
                {c.registered}<br />
                <span className="font-mono text-[13px] text-foreground">{c.companyNumberPrefix} {COMPANY.companyNumber}</span>
              </p>
            </CoordBlock>

            <CoordBlock icon={Mail} label={c.generalLabel}>
              <a href={`mailto:${COMPANY.emails.general}`} className="font-mono text-[13px] text-foreground hover:text-emerald-400 transition-colors" data-testid="email-general">
                {COMPANY.emails.general}
              </a>
            </CoordBlock>

            <CoordBlock icon={ScrollText} label={c.irLabel}>
              <a href={`mailto:${COMPANY.emails.investorRelations}`} className="font-mono text-[13px] text-foreground hover:text-emerald-400 transition-colors" data-testid="email-ir">
                {COMPANY.emails.investorRelations}
              </a>
            </CoordBlock>
          </div>
        </div>

        {/* Right — contact form */}
        <div className="md:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-6 border border-border p-6 md:p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("contact.name")}><Input required data-testid="contact-name" value={form.name} onChange={upd("name")} className="rounded-none h-11" /></Field>
              <Field label={t("contact.email")}><Input required type="email" data-testid="contact-email" value={form.email} onChange={upd("email")} className="rounded-none h-11" /></Field>
              <Field label={t("contact.company")}><Input data-testid="contact-company" value={form.company} onChange={upd("company")} className="rounded-none h-11" /></Field>
              <Field label={t("contact.phone")}><Input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className="rounded-none h-11" /></Field>
            </div>
            <Field label={t("contact.subject")}><Input required data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="rounded-none h-11" /></Field>
            <Field label={t("contact.message")}><Textarea required rows={6} data-testid="contact-message" value={form.message} onChange={upd("message")} className="rounded-none" /></Field>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="rounded-none h-11 px-8 text-[12px] tracking-widest uppercase">
              {loading ? "…" : t("contact.submit")}
            </Button>
          </form>
        </div>
      </div>
    </Section>
  );
}

const CoordBlock = ({ icon: I, label, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-8 h-8 rounded-full border border-border flex items-center justify-center shrink-0 mt-0.5">
      <I className="w-3.5 h-3.5 text-emerald-400" />
    </div>
    <div className="min-w-0">
      <p className="overline text-muted-foreground">{label}</p>
      <div className="mt-2">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline text-muted-foreground block mb-2">{label}</span>
    {children}
  </label>
);
